import React, { useEffect, useMemo, useState } from 'react';
import ReactStars from 'react-rating-stars-component';
import styled from 'styled-components';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';

import FirebaseImage from '../Media/FirebaseImage';
import { subscribePublishedReviews } from '../../Services/trustReviews';
import { useI18n } from '../../i18n/I18nProvider';

const ReviewList = ({ revieweeId, reviewerId, limit = 6, compact = false, title }) => {
  const { formatDate, t } = useI18n();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(Boolean(revieweeId || reviewerId));
  const [error, setError] = useState('');
  const heading = title || t('trust.verifiedReviews');

  useEffect(() => {
    setReviews([]);
    setError('');
    setLoading(Boolean(revieweeId || reviewerId));
    const unsubscribe = subscribePublishedReviews(
      { revieweeId, reviewerId },
      (next) => { setReviews(next); setLoading(false); },
      () => { setError(t('trust.reviewError')); setLoading(false); },
    );
    return unsubscribe;
  }, [revieweeId, reviewerId, t]);

  const visible = useMemo(() => reviews.slice(0, limit), [limit, reviews]);

  return (
    <Section $compact={compact}>
      <Heading>
        <div><RateReviewOutlinedIcon /><strong>{heading}</strong></div>
        <span>{t('trust.reviewCount', { count: reviews.length })}</span>
      </Heading>

      {loading ? (
        <State>{t('trust.reviewLoading')}</State>
      ) : error ? (
        <State $error>{error}</State>
      ) : visible.length === 0 ? (
        <State>{t('trust.reviewEmpty')}</State>
      ) : (
        <List>
          {visible.map((review) => (
            <Card key={review.id} $compact={compact}>
              <Author>
                <Avatar
                  src={review.reviewer_profile_image}
                  fallbackSrc="/images/user.svg"
                  alt=""
                />
                <div>
                  <strong>{review.reviewer_name || (review.reviewer_role === 'Client' ? t('trust.verifiedClient') : t('trust.verifiedProfessional'))}</strong>
                  <span>{review.project_title || t('trust.verifiedProject')} · {formatDate(review.published_at || review.submitted_at)}</span>
                </div>
                <Score>{Number(review.rating || 0).toFixed(1)}</Score>
              </Author>
              <ReactStars count={5} value={Number(review.rating) || 0} size={compact ? 16 : 19} edit={false} isHalf activeColor="#c0a375" />
              {review.comment && <Comment>{review.comment}</Comment>}
            </Card>
          ))}
        </List>
      )}
    </Section>
  );
};

const Section = styled.section`
  min-width: 0;
  display: grid;
  gap: ${({ $compact }) => ($compact ? '.55rem' : '.8rem')};
`;
const Heading = styled.header`
  display:flex;align-items:center;justify-content:space-between;gap:.75rem;
  >div{display:flex;align-items:center;gap:.45rem;min-width:0;} svg{font-size:1.05rem;} strong{font-size:.86rem;} span{color:var(--color-textMuted);font-size:.7rem;white-space:nowrap;}
`;
const List = styled.div`display:grid;gap:.65rem;`;
const Card = styled.article`
  min-width:0;padding:${({$compact})=>$compact?'.7rem':'.9rem'};border:1px solid #e8e3db;border-radius:.9rem;background:#faf9f7;
`;
const Author = styled.div`
  display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center;gap:.55rem;min-width:0;
  >div{display:grid;min-width:0;} strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:.78rem;} span{color:#777068;font-size:.64rem;line-height:1.35;}
`;
const Avatar = styled(FirebaseImage)`width:2rem;height:2rem;border-radius:50%;object-fit:cover;background:#eee9e2;`;
const Score = styled.strong`padding:.25rem .42rem;border-radius:999px;background:#fff2cf;color:#6e5016;font-size:.7rem;`;
const Comment = styled.p`margin:.45rem 0 0;color:#403c37;font-size:.74rem;line-height:1.55;white-space:pre-wrap;`;
const State = styled.p`margin:0;padding:.75rem;border:1px dashed ${({$error})=>$error?'#efc8c3':'#ddd5ca'};border-radius:.8rem;color:${({$error})=>$error?'#8c332c':'#777068'};font-size:.72rem;text-align:center;`;

export default ReviewList;
